import { z } from 'zod'

import {
  PROFILE_FIELD_TYPES,
  standardSettings,
  type FieldOption,
  type ProfileField,
  type ProfileFieldType,
  type RegistrationForm,
  type StandardSettings,
} from '@ems/contracts/profile-fields'

import { api } from '#admin/lib/api-client.ts'

/**
 * The registration form's settings, as `/v1/identity/admin/profile-fields` sends them.
 *
 * Each feature declares its own schemas (`REGISTRATIONS_UI_PLAN.md` §3): the client is
 * hand-rolled and parses every response, so a response this does not recognize is a
 * `response_shape` error rather than a page that renders half a form.
 */

const optionSchema = z.object({ value: z.string(), label: z.string() })

const fieldSchema = z.object({
  id: z.string(),
  key: z.string(),
  label: z.string(),
  helpText: z.string().nullable(),
  type: z.enum(PROFILE_FIELD_TYPES),
  options: z.array(optionSchema),
  required: z.boolean(),
  sensitive: z.boolean(),
  ticketTypeIds: z.array(z.string()),
  sortOrder: z.number(),
  archived: z.boolean(),
  answerCount: z.number(),
  optionCounts: z.record(z.string(), z.number()),
})

const standardSettingSchema = z.object({
  label: z.string().nullable(),
  shown: z.boolean(),
  required: z.boolean(),
})

const formSchema = z.object({
  fields: z.array(fieldSchema),
  standard: z.record(z.string(), standardSettingSchema),
  defaultCountry: z.string(),
})

export type FormField = z.infer<typeof fieldSchema>
export type FormSettings = {
  fields: FormField[]
  standard: StandardSettings
  defaultCountry: string
}

const base = '/v1/identity/admin/profile-fields'

function toSettings(raw: z.infer<typeof formSchema>): FormSettings {
  return {
    fields: raw.fields,
    standard: standardSettings(raw.standard),
    defaultCountry: raw.defaultCountry,
  }
}

export async function fetchFormSettings(signal?: AbortSignal): Promise<FormSettings> {
  return toSettings(await api.get(base, { schema: formSchema, ...(signal ? { signal } : {}) }))
}

/** The shape the shared renderer and the shared checks read. */
export function asRegistrationForm(settings: FormSettings): RegistrationForm {
  return {
    fields: settings.fields.map((field): ProfileField => ({
      key: field.key,
      label: field.label,
      helpText: field.helpText,
      type: field.type,
      options: field.options,
      required: field.required,
      sensitive: field.sensitive,
      ticketTypeIds: field.ticketTypeIds,
      archived: field.archived,
    })),
    standard: settings.standard,
    defaultCountry: settings.defaultCountry,
  }
}

export type FieldDraft = {
  label: string
  helpText: string
  type: ProfileFieldType
  options: FieldOption[]
  required: boolean
  sensitive: boolean
  ticketTypeIds: string[]
}

function definitionBody(draft: FieldDraft) {
  return {
    label: draft.label.trim(),
    helpText: draft.helpText.trim() === '' ? null : draft.helpText.trim(),
    type: draft.type,
    options: draft.options.map((option) => ({
      value: option.value.trim(),
      label: option.label.trim(),
    })),
    required: draft.required,
    sensitive: draft.sensitive,
    ticketTypeIds: draft.ticketTypeIds,
  }
}

export function createField(key: string, draft: FieldDraft) {
  return api.post(base, { schema: fieldSchema, body: { key, ...definitionBody(draft) } })
}

export function updateField(id: string, draft: FieldDraft) {
  return api.patch(`${base}/${id}`, { schema: fieldSchema, body: definitionBody(draft) })
}

export function archiveField(id: string) {
  return api.post(`${base}/${id}/archive`, { schema: fieldSchema })
}

export function restoreField(id: string) {
  return api.post(`${base}/${id}/restore`, { schema: fieldSchema })
}

export function reorderFields(ids: readonly string[]) {
  return api.put(`${base}/order`, { schema: z.undefined(), body: { ids } })
}

export async function saveStandard(standard: StandardSettings, defaultCountry: string) {
  return toSettings(
    await api.put(`${base}/standard`, { schema: formSchema, body: { standard, defaultCountry } }),
  )
}

// --- vocabulary ------------------------------------------------------------------------------

/**
 * What each answer type is called, and what it is for — the words the type picker shows.
 * Named by what the registrant does, not by what is stored.
 */
export const TYPE_DETAILS: Record<ProfileFieldType, { label: string; description: string }> = {
  text: { label: 'Short answer', description: 'A line of text, like a city or an ID number.' },
  long_text: { label: 'Paragraph', description: 'Several lines, like a bio or special needs.' },
  select: { label: 'One choice', description: 'Pick one from a list, like a title.' },
  multi_select: { label: 'Several choices', description: 'Tick any from a list, like topics.' },
  date: { label: 'Date', description: 'A calendar date, like a date of birth.' },
  number: { label: 'Number', description: 'A number, like years of experience.' },
  yes_no: { label: 'Yes or no', description: 'A single yes or no question.' },
  country: { label: 'Country', description: 'A searchable list of countries.' },
  phone: { label: 'Phone number', description: 'A number with its country code.' },
  email: { label: 'Email address', description: 'An address that is checked as typed.' },
  file: { label: 'File upload', description: 'A PDF or photo, like an ID card. Up to 10 MB.' },
}
